import React from "react";
import { GraduationCap, Award, Users, ArrowUpRight, Calendar } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { mapToCanonicalPracticeArea } from "../data/practiceAttorneyMap";
import { attorneyImgStyle } from "../lib/attorneyImage";
import { useConsultNav } from "../lib/consultSource";

function PracticeAreaBadge({ rawTitle, onPracticeClick }) {
  const canonical = mapToCanonicalPracticeArea(rawTitle);
  if (canonical) {
    return (
      <button
        type="button"
        onClick={() => onPracticeClick(rawTitle)}
        className="group inline-flex items-center gap-1.5 rounded-none border border-[#b8924a]/50 bg-white px-3 py-1 text-xs font-normal text-[#1a2c4e] hover:bg-[#b8924a] hover:text-white hover:border-[#b8924a] focus:outline-none focus:ring-2 focus:ring-[#b8924a]/40 transition-colors"
        aria-label={`View ${canonical} practice area`}
        title={`View ${canonical} practice area`}
      >
        {rawTitle}
        <ArrowUpRight className="w-3 h-3 opacity-60 group-hover:opacity-100" />
      </button>
    );
  }
  return (
    <span className="inline-flex items-center rounded-none border border-[#4a6d8c]/40 bg-white px-3 py-1 text-xs font-normal text-[#1a2c4e]">
      {rawTitle}
    </span>
  );
}

function BioSection({ icon: Icon, label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 text-[#1a2c4e] font-semibold uppercase tracking-wider text-xs mb-2">
        <Icon className="w-4 h-4 text-[#b8924a]" /> {label}
      </div>
      <ul className="text-[#3a4d6b] space-y-1">
        {items.map((it) => (
          <li key={it}>&mdash; {it}</li>
        ))}
      </ul>
    </div>
  );
}

function renderBioWithLinks(text) {
  if (!text) return null;
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <a
        key={`lnk-${key++}`}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#b8924a] hover:text-[#1a2c4e] underline underline-offset-2"
      >
        {match[1]}
      </a>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

export default function AttorneyBioDialog({ attorney, open, onClose, onPracticeClick }) {
  const goToConsult = useConsultNav();
  const scheduleConsult = () => {
    onClose();
    goToConsult(`${attorney.name} — attorney profile pop-out`);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 bg-[#fbf9f6] border-stone-200">
        {attorney && (
          <div className="grid md:grid-cols-5">
            <div className="md:col-span-2 bg-[#1a2c4e] p-8">
              <div className="aspect-[4/5] overflow-hidden bg-stone-700">
                <img
                  src={attorney.image}
                  alt={attorney.name}
                  className="w-full h-full object-cover"
                  style={attorneyImgStyle(attorney)}
                />
              </div>
              <button
                type="button"
                onClick={scheduleConsult}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 h-12 px-6 bg-[#b8924a] hover:bg-[#a07f3e] text-white uppercase tracking-[0.2em] text-[11px] font-medium border border-[#b8924a] transition-colors"
                data-testid="attorney-dialog-consult-button"
              >
                <Calendar className="w-4 h-4" /> Schedule a Consult
              </button>
            </div>
            <div className="md:col-span-3 p-8 md:p-10">
              <DialogHeader>
                <DialogTitle asChild>
                  <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#b8924a]">{attorney.title}</div>
                    <h3 className="font-serif text-4xl text-[#1a2c4e] mt-2 mb-4 leading-tight">{attorney.name}</h3>
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="h-px w-12 bg-[#b8924a] mb-5" />

              <p className="text-[#3a4d6b] leading-relaxed mb-6">{renderBioWithLinks(attorney.bio)}</p>

              <div className="space-y-5 text-sm">
                <div>
                  <div className="flex items-center gap-2 text-[#1a2c4e] font-semibold uppercase tracking-wider text-xs mb-2">
                    <Award className="w-4 h-4 text-[#b8924a]" /> Practice Areas
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {attorney.practiceAreas.map((p) => (
                      <PracticeAreaBadge key={p} rawTitle={p} onPracticeClick={onPracticeClick} />
                    ))}
                  </div>
                </div>

                <BioSection icon={GraduationCap} label="Education" items={attorney.education} />
                <BioSection icon={Award} label="Honors & Recognition" items={attorney.honors} />
                <BioSection icon={Users} label="Community" items={attorney.community} />
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
